import React from "react";
import { motion } from "framer-motion";
import { Star, Facebook, Instagram, Linkedin } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const testimonials = [
  {
    name: "Gopal Pathak",
    //role: "Home Owner",
    rating: 5,
    content:
      "The team did an excellent job with our house design. Truly exceeded expectations!",
    socials: {
      facebook: "#",
      instagram: "#",
      linkedin: "#",
    },
  },
  {
    name: "Prashant Jaat",
    //role: "Interior Designer",
    rating: 5,
    content:
      "They perfectly captured the essence of modern architecture in my project.",
    socials: {
      facebook: "#",
      instagram: "#",
      linkedin: "#",
    },
  },
  {
    name: "Mamata Sharma",
    //role: "Business Owner",
    rating: 4,
    content:
      "Professional, responsive, and highly creative team. Great experience overall.",
    socials: {
      facebook: "#",
      instagram: "#",
      linkedin: "#",
    },
  },
  {
    name: "Abhishek Sikarwar",
    //role: "Entrepreneur",
    rating: 5,
    content:
      "My office space has been completely transformed. Clients love the new design!",
    socials: {
      facebook: "#",
      instagram: "#",
      linkedin: "#",
    },
  },
  {
    name: "Manjit Kumar",
    //role: "Engineer",
    rating: 4,
    content:
      "They handled every detail with precision and care. Highly recommended!",
    socials: {
      facebook: "#",
      instagram: "#",
      linkedin: "#",
    },
  },
  {
    name: "Vikash Sharma",
    //role: "Doctor",
    rating: 5,
    content:
      "Very happy with the service and creativity. My clinic looks stunning now!",
    socials: {
      facebook: "#",
      instagram: "#",
      linkedin: "#",
    },
  },
];

const TestimonialsSection = () => {
  return (
    <section className="section-padding relative bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-black">
      <div className="max-w-7xl mx-auto container-padding relative z-10">

        {/* Glow Background */}
        <div className="absolute inset-0 blur-3xl opacity-30 bg-gradient-to-r from-yellow-400 via-orange-500 to-pink-500 rounded-full -z-10" />

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-extrabold font-heading text-gray-900 dark:text-white mb-4">
            Client{" "}
            <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
              Testimonials
            </span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Hear what our satisfied clients say about our architectural services
          </p>
        </motion.div>

        {/* Swiper */}
        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          spaceBetween={30}
          slidesPerView={1}
          loop={true}
          autoplay={{ delay: 3500, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          navigation={true}
          breakpoints={{
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
        >
          {testimonials.map((testimonial, index) => (
            <SwiperSlide key={testimonial.name}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative bg-white/20 dark:bg-gray-800/30 backdrop-blur-md border border-gray-200/30 dark:border-gray-700/30 rounded-2xl p-8 shadow-xl hover:shadow-yellow-500/20 hover:scale-[1.02] transition-all duration-500"
              >
                {/* Stars */}
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-yellow-400 drop-shadow-sm"
                    />
                  ))}
                </div>

                {/* Review */}
                <p className="text-gray-700 dark:text-gray-300 mb-6 italic leading-relaxed relative">
                  <span className="text-5xl text-yellow-400 font-serif absolute -left-4 top-0">
                    “
                  </span>
                  {testimonial.content}
                  <span className="text-5xl text-yellow-400 font-serif absolute -right-4 bottom-0">
                    ”
                  </span>
                </p>

                <hr className="border-t border-gray-200 dark:border-gray-700 my-4" />

                {/* Client Info (IMAGE REMOVED ONLY) */}
                <div className="flex items-center mt-4 justify-between">
                  <div className="flex items-center">
                    <div className="text-left">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {testimonial.name}
                      </h4>
                      {/* <p className="text-sm text-gray-500 dark:text-gray-400">
                        {testimonial.role}
                      </p> */}
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="flex space-x-3">
                    <a
                      href={testimonial.socials.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-yellow-400 hover:text-white transition-colors"
                    >
                      <Facebook size={18} />
                    </a>

                    <a
                      href={testimonial.socials.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-pink-500 hover:text-white transition-colors"
                    >
                      <Instagram size={18} />
                    </a>

                    <a
                      href={testimonial.socials.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-blue-600 hover:text-white transition-colors"
                    >
                      <Linkedin size={18} />
                    </a>
                  </div>
                </div>
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default TestimonialsSection;
